import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, useDispatch, useSelector, useStore } from 'react-redux';

import styles from './Robot.module.scss';

import actions from '@/actions';
import RobotMarker from '@/components/ui/map/RobotMarker';
import OlMap from '@/helpers/OlMap';

const cx = classNames.bind(styles);

const MarkerContent = ({ data: robot }) => {
  const showLabel = useSelector((state) => state.robotOptions[robot.id]?.label.visible);
  const globalPosition = useSelector((state) => state.telemetry[robot.id]?.globalPosition);
  const vfrHud = useSelector((state) => state.telemetry[robot.id]?.vfrHud);
  const robotRef = useRef();
  const altitudeRef = useRef();
  const speedRef = useRef();

  useEffect(() => {
    if (!globalPosition) return;

    robotRef.current.style.transform = `rotate(${globalPosition.hdg}deg)`;
    if (showLabel) {
      altitudeRef.current.innerText = globalPosition.ralt.toFixed(2);
    }
  }, [showLabel, globalPosition]);

  useEffect(() => {
    if (!vfrHud) return;
    if (!showLabel) return;

    speedRef.current.innerText = vfrHud.groundspeed.toFixed(2);
  }, [showLabel, vfrHud]);

  return (
    <div className={cx('container')}>
      <div ref={robotRef}>
        <RobotMarker data={robot} />
      </div>
      {showLabel && (
        <div className={cx('panel')}>
          <div className={cx('wrapper')}>
            <div className={cx('info')}>
              <span ref={altitudeRef}>N/A</span>
              <span className={cx('unit')}>m</span>
            </div>
            <div className={cx('info')}>
              <span ref={speedRef}>N/A</span>
              <span className={cx('unit')}>m/s</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Robot = ({ data: robot }) => {
  const dispatch = useDispatch();
  const globalPosition = useSelector((state) => state.telemetry[robot.id].globalPosition);
  const store = useStore();
  const element = useRef();

  useMountEffect(() => {
    element.current = getMarkerElement();

    element.current.addEventListener('click', (e) => {
      // 수정자 키 조합 없이 단일 선택 시
      if (!e.ctrlKey && !e.metaKey) {
        const { targetRobotIds } = store.getState().robot;
        if (targetRobotIds.length === 1 && targetRobotIds[0] === robot.id) return;

        dispatch(actions.robot.reset());
      }

      dispatch(actions.robot.toggle(robot.id));
    });
    element.current.addEventListener('mouseenter', () => {
      dispatch(actions.callout.setText(robot.name));
    });
    element.current.addEventListener('mouseleave', () => {
      dispatch(actions.callout.setText(null));
    });

    return () => {
      OlMap.removeRobot(robot.id);
    };
  });

  useUpdateEffect(() => {
    const { lat, lng } = globalPosition;

    OlMap.addRobot(robot.id, element.current, { lat, lng });
  }, [globalPosition]);

  const getMarkerElement = () => {
    const content = document.createElement('div');
    content.setAttribute('data-robot-id', robot.id);

    createRoot(content).render(
      <Provider store={store}>
        <MarkerContent data={robot} />
      </Provider>
    );
    return content;
  };
};

export default Robot;
